import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './Button';
import { logError } from '../../services/logging/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary to catch and display React errors.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError('Error caught by boundary', { error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error fallback content for the boundary.
 */
function ErrorFallback({
  error,
  onRetry,
  onReload,
}: {
  error: Error | null;
  onRetry: () => void;
  onReload: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-dark-100">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <ErrorIcon />
        </div>

        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>

        <p className="text-gray-400 mb-6">
          An unexpected error occurred. Please try again or reload the page.
        </p>

        {error && (
          <div className="bg-dark-300 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-red-400 font-mono break-all">
              {error.message}
            </p>
          </div>
        )}

        <div className="flex gap-4 justify-center">
          <Button variant="secondary" onClick={onRetry}>
            Try Again
          </Button>
          <Button onClick={onReload}>
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}

function ErrorIcon() {
  return (
    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
